/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { convertDDMMYYYtoYYYYMMDD } from '@futrli/predict/utils/dates';
import { ForecastPreviousActualsProps } from '@futrli/predict/forecast/create-forecast/components/form-elements/forecast-previous-actuals';
import { useForecastStartDateField } from '@futrli/predict/forecast/create-forecast/hooks/useForecastStartDateField';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { ForecastDurationProps } from '../../../components/form-elements/forecast-duration';
import { ForecastFormElementIds } from '../../../components/form-elements/types';
import { ForecastNameProps } from '../../../components/form-elements/forecast-name';
import { formatDateForForm, getForecastFormType } from '../../forecast-form-utils';
import {
  ForecastToUpdate,
  UpdateForecastFormType,
  UpdateForecastRequestData
} from '../types';
import {
  getDurationFromWindowSpec,
  getFixedForecastRequestData,
  getInitialDuration,
  getIsDirty,
  getRollingForecastRequestData
} from './update-forecast-form-utils';

/**
 * Manages the editable state, validation, dirty state, and PATCH payload for the update forecast form.
 *
 * @param forecast - Forecast list item loaded for editing.
 * @returns Form state and handlers consumed by the update forecast sidebar.
 */
export const useUpdateForecastForm = (forecast: ForecastToUpdate | undefined) => {
  const [duration, setDuration] = useState('');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [formType, setFormType] = useState<UpdateForecastFormType>('legacy');
  const [name, setName] = useState('');
  const [previousActuals, setPreviousActuals] = useState(false);

  const intl = useIntl();
  const hasBeenValidated = useRef(false);
  const initialDuration = useRef('');
  const initialForecastFromDate = useRef('');
  const initialFrom = useRef('');
  const initialName = useRef('');
  const initialPreviousActuals = useRef(false);
  const { from, getStartDate, maxDate, minDate, onChangeFrom, setFrom, validateStartDate } =
    useForecastStartDateField({ setErrors });

  useEffect(() => {
    if (!forecast) {
      return;
    }

    const nextFormType = getForecastFormType(forecast);
    const nextPreviousActuals =
      nextFormType === 'rolling' && forecast.window_spec?.kind === 'full-financial-years-with-actuals';
    const nextFrom = formatDateForForm(forecast.window_from || forecast.forecast_from_date);

    initialDuration.current = getInitialDuration(forecast, nextFormType);
    initialForecastFromDate.current = forecast.forecast_from_date ?? '';
    initialFrom.current = nextFrom;
    initialName.current = forecast.name ?? '';
    initialPreviousActuals.current = nextPreviousActuals;

    setDuration(getDurationFromWindowSpec(forecast, nextFormType));
    setErrors({});
    setFormType(nextFormType);
    setFrom(nextFrom);
    setName(forecast.name || '');
    setPreviousActuals(nextPreviousActuals);
    hasBeenValidated.current = false;
  }, [forecast, setFrom]);

  /** Validates the fields visible for the active forecast form type. */
  const validate = useCallback(() => {
    const newErrors: WizardFormErrors = {};

    if (!name.trim().length) {
      newErrors.name = {
        id: ForecastFormElementIds.Name,
        message:
          formType === 'budget'
            ? intl.formatMessage({
              id: 'predict.create_forecast.validation.budget_name_is_required',
              defaultMessage: 'Budget name is required'
            })
            : intl.formatMessage({
              id: 'predict.create_forecast.validation.forecast_name_is_required',
              defaultMessage: 'Forecast name is required'
            })
      };
    }

    if (formType !== 'rolling') {
      const startDateError = validateStartDate();

      if (startDateError) {
        newErrors.from = startDateError;
      }
    }

    if ((formType === 'fixed' || formType === 'rolling') && !duration) {
      newErrors.duration = {
        id: ForecastFormElementIds.Duration,
        message: intl.formatMessage({
          id: 'predict.create_forecast.validation.please_select_an_option',
          defaultMessage: 'Please select an option'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.values(newErrors).length === 0;
  }, [duration, formType, intl, name, validateStartDate]);

  /** Revalidates changes after the user has attempted submission once. */
  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  /** Stores the selected fixed-year or financial-year duration. */
  const onChangeDuration: ForecastDurationProps['onChange'] = useCallback((e: any) => {
    setDuration(e.target.value);
  }, []);

  /** Stores the edited forecast or budget name. */
  const onChangeName: ForecastNameProps['onChange'] = useCallback((e: any) => {
    setName(e.target.value);
  }, []);

  /** Stores whether a rolling forecast should display previous actuals. */
  const onChangePreviousActuals: ForecastPreviousActualsProps['onChange'] = useCallback((e: any) => {
    setPreviousActuals(e.target.checked);
  }, []);

  /** Builds the minimal PATCH payload for the active forecast form type. */
  const getRequestData = useCallback((): UpdateForecastRequestData => {
    const latestStartDate = getStartDate();

    if (formType === 'rolling') {
      return getRollingForecastRequestData({
        duration,
        initialDuration: initialDuration.current,
        initialName: initialName.current,
        initialPreviousActuals: initialPreviousActuals.current,
        name,
        previousActuals
      });
    }

    if (formType === 'fixed') {
      return getFixedForecastRequestData({
        duration,
        from: latestStartDate,
        initialDuration: initialDuration.current,
        initialForecastFromDate: initialForecastFromDate.current,
        initialFrom: initialFrom.current,
        initialName: initialName.current,
        name
      });
    }

    if (formType === 'budget') {
      return getFixedForecastRequestData({
        duration: '1',
        from: latestStartDate,
        initialDuration: '1',
        initialForecastFromDate: initialForecastFromDate.current,
        initialFrom: initialFrom.current,
        initialName: initialName.current,
        name,
        isBudget: true
      });
    }

    return {
      name,
      forecast_from_date: convertDDMMYYYtoYYYYMMDD(latestStartDate) ?? ''
    };
  }, [duration, formType, getStartDate, name, previousActuals]);

  const isDirty = getIsDirty({
    duration,
    formType,
    from,
    initialDuration: initialDuration.current,
    initialFrom: initialFrom.current,
    initialName: initialName.current,
    initialPreviousActuals: initialPreviousActuals.current,
    name,
    previousActuals
  });

  return {
    duration,
    errors,
    formType,
    from,
    isDirty,
    maxDate,
    minDate,
    name,
    previousActuals,
    getRequestData,
    onChangeDuration,
    onChangeFrom,
    onChangeName,
    onChangePreviousActuals,
    revalidate,
    setDuration,
    setFrom,
    validate
  };
};
